import React from 'react';
import type { NotComputedMap } from '../services/notComputed';
import { 
  CheckCircle, 
  AlertCircle, 
  XCircle, 
  AlertTriangle, 
  FileText, 
  Upload, 
  ChevronRight,
  Shield,
  Globe,
  TrendingDown,
  Clock,
  Zap
} from 'lucide-react';

// Types
interface CriticalCheck {
  name: string;
  status: 'PASS' | 'FAIL' | 'N/A' | 'WARNING' | 'PENDING';
  critical: boolean;
  detail: string;
  [key: string]: any;
}

interface ValidationCheck {
  name: string;
  status: 'PASS' | 'FAIL' | 'PENDING' | 'WARNING';
  critical: boolean;
  detail: string;
  [key: string]: any;
}

interface Warning {
  type: string;
  severity: 'high' | 'medium' | 'low';
  title: string;
  message: string;
  recommendation: string;
  impact?: string;
  [key: string]: any;
}

interface Recommendation {
  priority: 'HIGH' | 'MEDIUM' | 'LOW';
  action: string;
  reason: string;
  benefit: string;
  cta: string;
  icon: string;
}

interface HealthAssessment {
  status: 'GREEN' | 'YELLOW' | 'RED';
  status_message: string;
  status_emoji: string;
  status_color: string;
  critical: CriticalCheck[];
  validation: ValidationCheck[];
  warnings: Warning[];
  recommendations: Recommendation[];
  summary: {
    critical_passed: string;
    validation_passed: string;
    warnings_count: number;
    recommendations_count: number;
    high_priority_count: number;
  };
  // WP14: null (+ not_computed reason) when not on file — never "Unknown" / a silent 0.
  client_name?: string | null;
  business_type?: string | null;
  final_claim_amount?: number | null;
  not_computed?: NotComputedMap;
}

// Status Badge Component
export const ClaimHealthBadge: React.FC<{ 
  status: string; 
  message?: string;
  compact?: boolean;
}> = ({ status, message, compact = false }) => {
  const config = {
    GREEN: {
      bg: 'transparent',
      text: 'var(--color-verified)',
      border: 'var(--color-verified)',
      label: 'Ready'
    },
    YELLOW: {
      bg: 'var(--color-needs-human-tint)',
      text: 'var(--color-brand)',
      border: 'var(--color-needs-human-tint-bd)',
      label: 'Needs Review'
    },
    RED: {
      bg: 'var(--color-needs-human-tint)',
      text: 'var(--color-brand)',
      border: 'var(--color-needs-human-tint-bd)',
      label: 'Incomplete'
    }
  }[status] || {
    bg: 'var(--color-bg-elevated)',
    text: 'var(--color-text-secondary)',
    border: 'var(--color-border)',
    label: 'Unknown'
  };

  const dotColor = status === 'GREEN' ? 'var(--color-verified)' : status === 'YELLOW' ? 'var(--color-warning)' : 'var(--color-blocker)';

  if (compact) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-medium" style={{ backgroundColor: config.bg, color: config.text }}>
        <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: dotColor }} />
        <span>{config.label}</span>
      </span>
    );
  }

  return (
    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border" style={{ backgroundColor: config.bg, borderColor: config.border }}>
      <span className="inline-block w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: dotColor }} />
      <div>
        <span className="font-bold" style={{ color: config.text }}>{config.label}</span>
        {message && <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>{message}</p>}
      </div>
    </div>
  );
};

// Status Icon Component
const StatusIcon: React.FC<{ status: string; className?: string }> = ({ status, className = 'w-5 h-5' }) => {
  switch (status) {
    case 'PASS':
      return <CheckCircle className={className} style={{ color: 'var(--color-verified)' }} />;
    case 'FAIL':
      return <XCircle className={className} style={{ color: 'var(--color-brand)' }} />;
    case 'WARNING':
      return <AlertCircle className={className} style={{ color: 'var(--color-brand)' }} />;
    case 'PENDING':
      return <Clock className={className} style={{ color: 'var(--color-text-dim)' }} />;
    case 'N/A':
      return <span className={className} style={{ color: 'var(--color-text-ghost)' }}>—</span>;
    default:
      return <AlertCircle className={className} style={{ color: 'var(--color-text-dim)' }} />;
  }
};

// Critical Requirements Section
const CriticalRequirements: React.FC<{ checks: CriticalCheck[] }> = ({ checks }) => {
  const passCount = checks.filter(c => c.status === 'PASS').length;
  const totalRequired = checks.filter(c => c.status !== 'N/A').length;

  return (
    <div className="rounded-lg border p-4" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
      <h4 className="font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
        <Shield className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
        Critical Requirements
        <span className="text-sm font-normal" style={{ color: 'var(--color-text-muted)' }}>
          ({passCount}/{totalRequired} passed)
        </span>
      </h4>
      <div className="space-y-2">
        {checks.map((check, idx) => (
          <div
            key={idx}
            className="flex items-center justify-between p-3 rounded-lg"
            style={{ backgroundColor:
              check.status === 'PASS' ? 'transparent' :
              check.status === 'FAIL' ? 'var(--color-needs-human-tint)' :
              check.status === 'WARNING' ? 'var(--color-needs-human-tint)' :
              'var(--color-bg-elevated)'
            }}
          >
            <div className="flex items-center gap-3">
              <StatusIcon status={check.status} />
              <div>
                <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{check.name}</p>
                <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{check.detail}</p>
              </div>
            </div>
            {check.status === 'FAIL' && (
              <button className="text-sm hover:opacity-80 font-medium flex items-center gap-1" style={{ color: 'var(--color-needs-human-text)' }}>
                <Upload className="w-4 h-4" />
                Upload
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Validation Checks Section
const ValidationChecks: React.FC<{ checks: ValidationCheck[] }> = ({ checks }) => {
  return (
    <div className="rounded-lg border p-4" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
      <h4 className="font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
        <CheckCircle className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />
        Validation Checks
      </h4>
      <div className="space-y-2">
        {checks.map((check, idx) => (
          <div
            key={idx}
            className="flex items-center justify-between p-3 rounded-lg"
            style={{ backgroundColor:
              check.status === 'PASS' ? 'transparent' :
              check.status === 'FAIL' ? 'var(--color-needs-human-tint)' :
              check.status === 'PENDING' ? 'var(--color-bg-elevated)' :
              'var(--color-needs-human-tint)'
            }}
          >
            <div className="flex items-center gap-3">
              <StatusIcon status={check.status} />
              <div>
                <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{check.name}</p>
                <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{check.detail}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Warnings Section
const WarningsSection: React.FC<{ warnings: Warning[] }> = ({ warnings }) => {
  if (warnings.length === 0) return null;

  return (
    <div className="rounded-lg border p-4" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
      <h4 className="font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--color-brand)' }}>
        <AlertTriangle className="w-5 h-5" />
        Warnings for Attorney/Adjuster
        <span className="text-sm font-normal">({warnings.length})</span>
      </h4>
      <div className="space-y-3">
        {warnings.map((warning, idx) => (
          <div
            key={idx}
            className="p-4 rounded-lg border-l-4"
            style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-brand)' }}
          >
            <div className="flex items-start gap-2">
              {warning.type === 'pre_event_decline' && <TrendingDown className="w-5 h-5 mt-0.5" style={{ color: 'var(--color-needs-human-text)' }} />}
              {warning.type === 'timing_mismatch' && <Clock className="w-5 h-5 mt-0.5" style={{ color: 'var(--color-needs-human-text)' }} />}
              {warning.type === 'rapid_recovery' && <Zap className="w-5 h-5 mt-0.5" style={{ color: 'var(--color-needs-human-text)' }} />}
              <div className="flex-1">
                <p className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>{warning.title}</p>
                <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>{warning.message}</p>
                {warning.recommendation && (
                  <p className="text-sm mt-2 flex items-center gap-1" style={{ color: 'var(--color-needs-human-text)' }}>
                    <ChevronRight className="w-4 h-4" />
                    {warning.recommendation}
                  </p>
                )}
                {warning.impact && (
                  <p className="text-xs mt-1 italic" style={{ color: 'var(--color-text-muted)' }}>{warning.impact}</p>
                )}
              </div>
              <span className="text-xs font-medium px-2 py-1 rounded" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>
                {warning.severity.toUpperCase()}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Recommendations Section
const RecommendationsSection: React.FC<{ 
  recommendations: Recommendation[];
  onAction?: (cta: string) => void;
}> = ({ recommendations, onAction }) => {
  if (recommendations.length === 0) return null;

  const groupedRecs = {
    HIGH: recommendations.filter(r => r.priority === 'HIGH'),
    MEDIUM: recommendations.filter(r => r.priority === 'MEDIUM'),
    LOW: recommendations.filter(r => r.priority === 'LOW')
  };

  return (
    <div className="rounded-lg border p-4" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
      <h4 className="font-semibold mb-4 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
        <FileText className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
        Recommendations
      </h4>

      {/* HIGH Priority */}
      {groupedRecs.HIGH.length > 0 && (
        <div className="mb-4">
          <p className="text-xs font-semibold uppercase mb-2" style={{ color: 'var(--color-needs-human-text)' }}>High Priority</p>
          <div className="space-y-2">
            {groupedRecs.HIGH.map((rec, idx) => (
              <div key={idx} className="p-4 border rounded-lg" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>{rec.action}</p>
                    <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>{rec.reason}</p>
                    <p className="text-sm mt-1 font-medium" style={{ color: 'var(--color-verified)' }}>
                      ✓ {rec.benefit}
                    </p>
                  </div>
                  {onAction && rec.cta && (
                    <button
                      onClick={() => onAction(rec.cta)}
                      className="px-3 py-1.5 text-sm font-medium rounded-lg hover:opacity-90 flex items-center gap-1"
                      style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                    >
                      {rec.cta.includes('upload') ? <Upload className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      {rec.cta.includes('upload') ? 'Upload' : 'Fix'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* MEDIUM Priority */}
      {groupedRecs.MEDIUM.length > 0 && (
        <div className="mb-4">
          <p className="text-xs font-semibold uppercase mb-2" style={{ color: 'var(--color-brand)' }}>Suggested</p>
          <div className="space-y-2">
            {groupedRecs.MEDIUM.map((rec, idx) => (
              <div key={idx} className="p-4 border rounded-lg" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
                <p className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>{rec.action}</p>
                <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>{rec.reason}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* LOW Priority */}
      {groupedRecs.LOW.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase mb-2" style={{ color: 'var(--color-needs-human-text)' }}>Optional Enhancements</p>
          <div className="space-y-2">
            {groupedRecs.LOW.map((rec, idx) => (
              <div key={idx} className="p-3 border rounded-lg" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
                <p className="font-medium text-sm" style={{ color: 'var(--color-text-primary)' }}>{rec.action}</p>
                <p className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>{rec.reason}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Main Traffic Light Health Card Component
export const ClaimHealthCard: React.FC<{
  assessment: HealthAssessment;
  compact?: boolean;
  onAction?: (cta: string) => void;
}> = ({ assessment, compact = false, onAction }) => {
  const { status, status_message, critical, validation, warnings, recommendations, summary } = assessment;

  // Compact version for dashboard cards
  if (compact) {
    return (
      <div className="rounded-lg border-l-4 p-4" style={{
        borderColor: status === 'GREEN' ? 'var(--color-verified)' : 'var(--color-brand)',
        backgroundColor: status === 'GREEN' ? 'transparent' : 'var(--color-needs-human-tint)'
      }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span
              className="inline-block w-4 h-4 rounded-full flex-shrink-0"
              style={{ backgroundColor: status === 'GREEN' ? 'var(--color-verified)' : status === 'YELLOW' ? 'var(--color-warning)' : 'var(--color-blocker)' }}
            />
            <div>
              <p className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                {status === 'GREEN' ? 'Ready' : status === 'YELLOW' ? 'Needs Review' : 'Incomplete'}
              </p>
              <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{status_message}</p>
            </div>
          </div>
          {summary && summary.high_priority_count > 0 && (
            <span className="px-2 py-1 text-xs font-medium rounded-full" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>
              {summary.high_priority_count} action{summary.high_priority_count > 1 ? 's' : ''} needed
            </span>
          )}
        </div>
        {warnings.length > 0 && (
          <p className="text-sm mt-2 flex items-center gap-1" style={{ color: 'var(--color-brand)' }}>
            <AlertTriangle className="w-4 h-4" />
            {warnings.length} warning{warnings.length > 1 ? 's' : ''} for review
          </p>
        )}
      </div>
    );
  }

  // Full detailed view
  return (
    <div className="rounded-xl shadow-sm overflow-hidden" style={{ backgroundColor: 'var(--color-bg-base)' }}>
      {/* Header */}
      <div className="p-6" style={{ backgroundColor: status === 'GREEN' ? 'var(--color-verified)' : 'var(--color-brand)' }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span
              className="inline-block w-8 h-8 rounded-full flex-shrink-0"
              style={{ backgroundColor: status === 'GREEN' ? 'var(--color-verified)' : status === 'YELLOW' ? 'var(--color-warning)' : 'var(--color-blocker)' }}
            />
            <div style={{ color: 'var(--color-on-brand)' }}>
              <h2 className="text-2xl font-bold">
                CLAIM HEALTH: {status === 'GREEN' ? 'READY' : status === 'YELLOW' ? 'NEEDS REVIEW' : 'INCOMPLETE'}
              </h2>
              <p className="mt-1" style={{ opacity: 0.9 }}>{status_message}</p>
            </div>
          </div>
          {summary && (
            <div className="text-right" style={{ color: 'var(--color-on-brand)' }}>
              <p className="text-sm opacity-90">Requirements: {summary.critical_passed}</p>
              <p className="text-sm opacity-90">Validation: {summary.validation_passed}</p>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-6 space-y-6">
        {/* Critical Requirements */}
        <CriticalRequirements checks={critical} />

        {/* Validation Checks */}
        <ValidationChecks checks={validation} />

        {/* Warnings */}
        <WarningsSection warnings={warnings} />

        {/* Recommendations */}
        <RecommendationsSection recommendations={recommendations} onAction={onAction} />
      </div>
    </div>
  );
};

export default ClaimHealthCard;
